import { BadgeTone } from "stories/Admin";

export type PushKind =
  | "MOSQUE"
  | "ALL"
  | "TOPIC"
  | "ADMINS"
  | "JANAZAH"
  | "JAMAT"
  | "DIRECT"
  | "USER"
  | "UNKNOWN";

export type PushStatus = "SENT" | "PARTIAL" | "FAILED";

export interface PushLogEntry {
  id: number;
  date: string | null;
  kind: PushKind | string;
  status: PushStatus | string;
  topic: string | null;
  orgNr: string | null;
  mosqueName: string | null;
  title: string | null;
  body: string | null;
  imgurl: string | null;
  sentBy: string | null;
  error: string | null;
  recipients: number | null;
  failed: number | null;
  /** Mottakeren av en push til én bruker (e-post, UID eller slutten av tokenet). */
  target?: string | null;
  /** Moskénavn per topic når push-en gikk til flere moské-topics (samme rekkefølge som topic). */
  topicNames?: PushLogTopic[] | null;
}

export interface PushLogTopic {
  orgNr: string;
  name: string | null;
}

export interface PushLogCount {
  kind: string;
  sent: number;
  failed: number;
}

export interface PushLogPage {
  items: PushLogEntry[];
  total: number;
  page: number;
  size: number;
  last30Days: PushLogCount[];
}

export const kindInfo: Record<string, { label: string; tone: BadgeTone }> = {
  MOSQUE: { label: "Moské", tone: "mint" },
  ALL: { label: "Alle brukere", tone: "gold" },
  ADMINS: { label: "Admins", tone: "lilac" },
  TOPIC: { label: "Tema", tone: "blue" },
  JANAZAH: { label: "Janazah", tone: "dark" },
  JAMAT: { label: "Stille jamat", tone: "grey" },
  DIRECT: { label: "Én bruker", tone: "dark" },
  USER: { label: "Til bruker", tone: "lilac" },
  UNKNOWN: { label: "Eldre", tone: "grey" },
};

export const kindLabel = (kind: string) => kindInfo[kind]?.label ?? kind;

export const statusInfo = (
  e: Pick<PushLogEntry, "status">,
): { label: string; tone: BadgeTone } =>
  e.status === "FAILED"
    ? { label: "Feilet", tone: "rose" }
    : e.status === "PARTIAL"
      ? { label: "Delvis", tone: "gold" }
      : { label: "Sendt", tone: "mint" };

/**
 * FCM-temaene en push gikk til. En moské som erstatter en annen sender til begge, og loggen lagrer dem som
 * «933279723 + 999304613_1».
 */
export const topicsOf = (topic: string | null | undefined): string[] =>
  (topic ?? "")
    .split(" + ")
    .map((t) => t.trim())
    .filter(Boolean);

/**
 * Moskeen, og moskeen den erstatter hvis push-en også gikk til følgerne der: «Ny moské + Gammel moské».
 * Navnene kommer fra backend (topicNames); mangler et navn, vises org.nr.
 */
const mosqueRecipients = (e: PushLogEntry) => {
  const topics = topicsOf(e.topic);
  const names = new Map(
    (e.topicNames ?? []).map((t) => [t.orgNr, t.name?.trim() || null]),
  );
  if (topics.length > 1) {
    return topics
      .map(
        (t, i) =>
          names.get(t) || (i === 0 || t === e.orgNr ? e.mosqueName : null) || t,
      )
      .join(" + ");
  }
  return e.mosqueName || e.orgNr || topics[0] || "Moské";
};

/** Hvem push-en gikk til, i ord. */
export const recipientText = (e: PushLogEntry) => {
  switch (e.kind) {
    case "MOSQUE":
      return mosqueRecipients(e);
    case "ALL":
      return "Alle brukere";
    case "ADMINS":
      return e.recipients != null
        ? `${e.recipients.toLocaleString("nb-NO")} admins`
        : "Alle admins";
    case "JANAZAH":
      return "Følgere av janazah-varsler";
    case "JAMAT":
      return e.mosqueName || e.orgNr || e.topic || "Moské";
    case "DIRECT":
      return e.target || "Én bruker";
    case "USER":
      return e.target || "Søkeren";
    case "TOPIC":
      return `Tema «${e.topic ?? "?"}»`;
    default:
      return e.mosqueName || "Ukjent";
  }
};

export const formatLogDate = (iso: string | null) => {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleString("nb-NO", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const buildQuery = (params: {
  kind: string;
  status: string;
  q: string;
  page: number;
  size: number;
}) => {
  const s = new URLSearchParams();
  if (params.kind === "SILENT") s.set("silent", "true");
  else if (params.kind) s.set("kind", params.kind);
  if (params.status) s.set("status", params.status);
  if (params.q.trim()) s.set("q", params.q.trim());
  s.set("page", String(params.page));
  s.set("size", String(params.size));
  return `/api/v1/admin/push-log?${s.toString()}`;
};
